import React, { useEffect } from "react";
import { Row, Col, Label, FormGroup } from "reactstrap";
import Select from "react-select";
import { getPropertyList } from "../TenantForm/Helpers/api";

// The hero band these sit on is `overflow: hidden`, so the menu has to be
// portalled to the body or it gets clipped behind the table below.
const menuStyles = {
  menu: (provided) => ({ ...provided, zIndex: 9999 }),
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
};

/**
 * Property / Building pickers, identical in behaviour to the Mastersheet's
 * PropertySelector: same labels, same placeholders, and a building must be
 * chosen whenever the property has any (there is no "all buildings" option).
 * A property with exactly one building selects it automatically.
 */
const DirectoryFilters = ({ userData, filterData, setFilterData }) => {
  useEffect(() => {
    if (!userData?.id) return;
    getPropertyList(userData).then((filteredData) => {
      if (!filteredData) return;
      // Take only the property list. The helper also returns a `building_list`,
      // but it is the FIRST property's buildings rather than the selected one's —
      // spreading it would offer buildings before any property was chosen.
      setFilterData((prev) => ({ ...prev, propertyList: filteredData.propertyList || [] }));
    });
  }, [userData?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatProperty = (item) => ({
    id: item._id,
    value: item.title,
    label: `${item.title}, ${item.location}`,
    building: item.building || [],
  });

  const formatProperties = () => (filterData.propertyList || []).map(formatProperty);

  const formatBuildings = () =>
    buildings.map((b) => ({
      value: b.buildingName,
      label: b.buildingName,
    }));

  const handlePropertySelectionChange = (property) => {
    const buildings = property?.building || [];
    setFilterData((prev) => ({
      ...prev,
      property_id: property?.id || "",
      property_name: property?.value || "",
      building_list: buildings,
      // Nothing to choose when there is only one — pick it so the directory
      // loads straight away.
      building_name: buildings.length === 1 ? buildings[0]?.buildingName || "" : "",
    }));
  };

  // A building only ever belongs to a chosen property, so both the picker and the
  // auto-select stay dormant until one is selected.
  const buildings = filterData.property_id ? filterData.building_list || [] : [];

  useEffect(() => {
    if (!filterData.property_id) return;
    if (buildings.length !== 1 || filterData.building_name) return;
    const only = buildings[0]?.buildingName || "";
    if (only) setFilterData((prev) => ({ ...prev, building_name: only }));
  }, [filterData.property_id, buildings.length, filterData.building_name]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBuildingSelectionChange = (building) => {
    setFilterData((prev) => ({
      ...prev,
      building_name: building?.value || "",
    }));
  };

  return (
    <Row>
      <Col md={5}>
        <Label>Property Name</Label>
        <Select
          value={
            filterData.property_id
              ? { value: filterData.property_name, label: filterData.property_name }
              : null
          }
          options={formatProperties()}
          placeholder="Search By Name"
          onChange={handlePropertySelectionChange}
          className="property-select"
          styles={menuStyles}
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />
      </Col>

      {buildings.length > 0 && (
        <Col md={4}>
          <FormGroup>
            <Label>Building Name</Label>
            <Select
              value={
                filterData.building_name
                  ? { value: filterData.building_name, label: filterData.building_name }
                  : null
              }
              options={formatBuildings()}
              placeholder="Search By Building Name"
              onChange={handleBuildingSelectionChange}
              className="building-select"
              styles={menuStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </FormGroup>
        </Col>
      )}
    </Row>
  );
};

export { DirectoryFilters };
